import { Component, OnInit, OnDestroy, Input, signal } from '@angular/core';
import { TrajectoryService } from '../../../shared/services/trajectory.service';
import { lastValueFrom } from 'rxjs';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { valuesIncreaseValidator } from 'src/app/shared/components/ng-table-grid/shared-grid.validators';
import { TrajectoryModel } from 'src/app/shared/models/trajectory.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import {
  UnitsGridColumn,
  UnitsGridPasteTransform
} from 'src/app/shared/components/units-grid-input/units-grid-input.models';

export function normalizeTrajectoryPastedRows(parsedRows: readonly (readonly string[])[]): readonly (readonly string[])[] {
    if (!parsedRows.length) {
        return parsedRows;
    }

    const uniqueRows = new Map<number, readonly string[]>();

    parsedRows.forEach((row) => {
        const firstValue = Number(row[0]);
        if (!Number.isFinite(firstValue)) {
            return;
        }

        const normalizedKey = Number(firstValue.toFixed(6));
        if (!uniqueRows.has(normalizedKey)) {
            uniqueRows.set(normalizedKey, row);
        }
    });

    return [...uniqueRows.values()].sort((a, b) => Number(a[0]) - Number(b[0]));
}

@Component({
    selector: 'app-trajectory-grid',
    template: `
      <app-tool-loader [isLoading]="isLoading()" [positionUnset]="true">

        <app-units-grid-input
          [hidden]="isLoading()"
          [tableTitle]="'Trajectory Input'"
          [rows]="trajectoryRows"
          [columns]="columns"
          [userUnits]="userUnits()"
          [enableScroll]="true"
          [autoHeight]="true"
          [autoHeightOffset]="45"
          [scrollHeight]="'600px'"
          [virtualScroll]="true"
          [virtualScrollItemSize]="28"
          [allowDeleteRows]="true"
          [minRows]="1"
          [createRow]="createTrajectoryRow"
          [beforePasteTransform]="trajectoryPasteTransform"
          [commitMode]="'debounced'"
          [commitDebounceMs]="300"
          (commitRequested)="onGridCommitRequested()"
          (rowDeleted)="onRowDeleted($event)"
          (rowsReset)="onRowsReset()"
        />

      </app-tool-loader>
    `,
    standalone: false
})
export class TrajectoryGridComponent implements OnInit, OnDestroy {

  private isSaving = false;
  private suppressRefreshUntil = 0;
  private lastSavedRowsSignature = '';

  //Used for component state
  @Input()
  public componentId: string;

  public isLoading = signal<boolean>(false);
  public userUnits = signal<UserUnitsModel | null>(null);
  public trajectories: TrajectoryModel[];
  public trajectoryRows = new FormArray<FormGroup>([]);
  public readonly columns: readonly UnitsGridColumn[] = [
    {
      kind: 'number',
      field: 'measuredDepth',
      header: 'MD',
      unitKey: 'longLengths',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'inclination',
      header: 'Inclination',
      unitSymbol: '°',
      min: 0,
      max: 89.99,
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'azimuth',
      header: 'Azimuth',
      unitSymbol: '°',
      min: 0,
      max: 359.99,
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'doglegSeverity',
      header: 'DLS',
      unitKey: 'doglegSeverity',
      editable: false,
      formatFractionDigits: 3
    },
    {
      kind: 'number',
      field: 'trueVerticalDepth',
      header: 'TVD',
      unitKey: 'longLengths',
      editable: false,
      formatFractionDigits: 2
    },
    {
      kind: 'number',
      field: 'northing',
      header: 'Northing',
      unitKey: 'longLengths',
      editable: false,
      formatFractionDigits: 2
    },
    {
      kind: 'number',
      field: 'easting',
      header: 'Easting',
      unitKey: 'longLengths',
      editable: false,
      formatFractionDigits: 2
    }
  ];

  public createTrajectoryRow = (): FormGroup => this.createTrajectoryFormGroup();

    public readonly trajectoryPasteTransform: UnitsGridPasteTransform = ({
    parsedRows,
    startColumnIndex,
    columns
  }) => {
    if (!parsedRows.length) {
      return parsedRows;
    }

    const transformedRows = parsedRows.map((row) => [...row]);

    transformedRows.forEach((row) => {
      row.forEach((cellValue, relativeColumnIndex) => {
        const absoluteColumnIndex = startColumnIndex + relativeColumnIndex;
        const column = columns[absoluteColumnIndex];
        if (!column) {
          return;
        }

        const numericValue = Number(cellValue);
        if (!Number.isFinite(numericValue)) {
          return;
        }

        if (column.field === 'inclination' && numericValue > 89.99) {
          row[relativeColumnIndex] = '89.99';
          return;
        }

        if (column.field === 'azimuth' && numericValue > 359.99) {
          row[relativeColumnIndex] = '359.99';
        }
      });
    });

    const normalizedRows = normalizeTrajectoryPastedRows(transformedRows);

    // Legacy trajectory behavior: when the pasted data includes the 0 MD anchor row,
    // keep the existing fixed first row and paste only the remaining survey rows.
    const measuredDepthColumn = columns[startColumnIndex];
    const firstDepth = Number(normalizedRows[0]?.[0]);
    if (measuredDepthColumn?.field === 'measuredDepth' && Number.isFinite(firstDepth) && firstDepth === 0) {
      return normalizedRows.slice(1);
    }

    return normalizedRows;
  };

  constructor(
    private _trajectoryService: TrajectoryService,
    private _storeService: StoreService,
    private _messenger: MediatorService,
    private _signalRService: SignalRService
  ) {
    this.isLoading.set(true);
  }

  async ngOnInit(): Promise<void> {
    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.userUnits.set(uu);
    this.trajectories = await lastValueFrom(this._trajectoryService.getTrajectoryPoints());
    this.rebuildRows(this.trajectories);
    this.lastSavedRowsSignature = this.getRowsSignature(this.trajectories ?? []);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.isLoading.set(false);
  }

  private async signalRfunc(data: { action: string, designId: string }) {
    if (data.action == PeriforOnChangeMessages.REFRESH_TRAJECTORY) {
      if (Date.now() < this.suppressRefreshUntil) {
        return;
      }

      this.trajectories = await lastValueFrom(this._trajectoryService.getTrajectoryPoints());
      this.lastSavedRowsSignature = this.getRowsSignature(this.trajectories ?? []);
      this.rebuildRows(this.trajectories);
    }
  }

  private getRowsSignature(rows: TrajectoryModel[]): string {
    return JSON.stringify(rows ?? []);
  }

  private createTrajectoryFormGroup(row?: Partial<TrajectoryModel>): FormGroup {
    return new FormGroup({
      measuredDepth: new FormControl(row?.measuredDepth ?? null, [Validators.required, valuesIncreaseValidator('measuredDepth')]),
      inclination: new FormControl(row?.inclination ?? null, [Validators.min(0), Validators.max(89.99), Validators.required]),
      azimuth: new FormControl(row?.azimuth ?? null, [Validators.min(0), Validators.max(359.99), Validators.required]),
      doglegSeverity: new FormControl(row?.doglegSeverity ?? null),
      trueVerticalDepth: new FormControl(row?.trueVerticalDepth ?? null),
      northing: new FormControl(row?.northing ?? null),
      easting: new FormControl(row?.easting ?? null)
    });
  }

  private isNullish(value: unknown): boolean {
    return value === null || value === undefined || value === '';
  }

  private isTrajectoryRowEmpty(row: Partial<TrajectoryModel> | null | undefined): boolean {
    if (!row) {
      return true;
    }

    return this.isNullish(row.measuredDepth)
      && this.isNullish(row.inclination)
      && this.isNullish(row.azimuth);
  }

  private isTrajectoryRowComplete(row: Partial<TrajectoryModel> | null | undefined): boolean {
    if (!row) {
      return false;
    }

    return !this.isNullish(row.measuredDepth)
      && !this.isNullish(row.inclination)
      && !this.isNullish(row.azimuth);
  }

  private ensureTrailingInputRow(): void {
    if (this.trajectoryRows.length === 0) {
      this.trajectoryRows.push(this.createTrajectoryFormGroup());
      return;
    }

    while (
      this.trajectoryRows.length > 1
      && this.isTrajectoryRowEmpty(this.trajectoryRows.at(this.trajectoryRows.length - 1)?.getRawValue())
      && this.isTrajectoryRowEmpty(this.trajectoryRows.at(this.trajectoryRows.length - 2)?.getRawValue())
    ) {
      this.trajectoryRows.removeAt(this.trajectoryRows.length - 1);
    }

    const lastRow = this.trajectoryRows.at(this.trajectoryRows.length - 1)?.getRawValue() as Partial<TrajectoryModel> | undefined;
    if (!this.isTrajectoryRowEmpty(lastRow)) {
      this.trajectoryRows.push(this.createTrajectoryFormGroup());
    }
  }

  private hasIncompleteStartedRows(): boolean {
    return this.trajectoryRows.controls.some((rowControl) => {
      const row = (rowControl as FormGroup).getRawValue() as Partial<TrajectoryModel>;
      if (this.isTrajectoryRowEmpty(row)) {
        return false;
      }

      return !this.isTrajectoryRowComplete(row) || rowControl.invalid;
    });
  }

  private rebuildRows(rows: TrajectoryModel[]): void {
    this.trajectoryRows.clear();

    (rows ?? []).forEach((row) => {
      this.trajectoryRows.push(this.createTrajectoryFormGroup(row));
    });

    this.ensureTrailingInputRow();

    this.disableFirstMeasuredDepth();
  }

  private disableFirstMeasuredDepth(): void {
    const firstRow = this.trajectoryRows.at(0) as FormGroup | undefined;
    const measuredDepth = firstRow?.get('measuredDepth');
    if (!measuredDepth) {
      return;
    }

    measuredDepth.disable({ emitEvent: false });
  }

  private buildPayloadRows(): TrajectoryModel[] {
    const rows = this.trajectoryRows.getRawValue() as TrajectoryModel[];
    return rows.filter((row) => this.isTrajectoryRowComplete(row));
  }

  private saveTrajectory(): void {
    if (this.isLoading() || this.isSaving || this.hasIncompleteStartedRows()) {
      return;
    }

    const payload = this.buildPayloadRows();
    const payloadSignature = this.getRowsSignature(payload);
    if (payloadSignature === this.lastSavedRowsSignature) {
      return;
    }

    this.isSaving = true;

    this._trajectoryService.updateTrajectory(payload).subscribe({
      next: (updatedRows) => {
        const rowsToApply = (updatedRows ?? payload) as TrajectoryModel[];
        this.trajectories = [...rowsToApply];
        this.applySavedRows(rowsToApply);
        this.lastSavedRowsSignature = this.getRowsSignature(rowsToApply);
        this.suppressRefreshUntil = Date.now() + 2500;
        this._messenger.publish(new DataSavedMessage('Trajectory Input'));
      },
      complete: () => {
        this.isSaving = false;
      },
      error: () => {
        this.isSaving = false;
      }
    });
  }

  private applySavedRows(rows: TrajectoryModel[]): void {
    const dataRows = this.trajectoryRows.controls.slice(0, -1) as FormGroup[];
    if (dataRows.length !== rows.length) {
      this.rebuildRows(rows);
      return;
    }

    rows.forEach((row, rowIndex) => {
      dataRows[rowIndex].patchValue(row, { emitEvent: false });
    });
    this.ensureTrailingInputRow();
    this.disableFirstMeasuredDepth();
  }

  public onGridCommitRequested(): void {
    this.ensureTrailingInputRow();
    this.disableFirstMeasuredDepth();
    this.saveTrajectory();
  }

  public onRowsReset(): void {
    const previousTotalDepth = this.trajectories?.length
      ? this.trajectories[this.trajectories.length - 1]?.measuredDepth ?? 0
      : 0;

    this.trajectoryRows.clear();
    this.trajectoryRows.push(this.createTrajectoryFormGroup({
      measuredDepth: 0,
      inclination: 0,
      azimuth: 0,
      doglegSeverity: 0,
      trueVerticalDepth: 0,
      northing: 0,
      easting: 0
    }));

    if (previousTotalDepth > 0) {
      this.trajectoryRows.push(this.createTrajectoryFormGroup({
        measuredDepth: previousTotalDepth,
        inclination: 0,
        azimuth: 0,
        doglegSeverity: 0,
        trueVerticalDepth: 0,
        northing: 0,
        easting: 0
      }));
    }

    this.disableFirstMeasuredDepth();
    this.ensureTrailingInputRow();
    this.onGridCommitRequested();
  }

  public onRowDeleted(rowIndex: number): void {
    if (rowIndex < 0 || rowIndex >= this.trajectoryRows.length || rowIndex === 0) {
      return;
    }

    this.trajectoryRows.removeAt(rowIndex);
    this.disableFirstMeasuredDepth();
    this.onGridCommitRequested();
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}

